import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, IconButton, Alert, Grid, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import SectionCard from '../../../components/SectionCard';
import InvButton from '../../../components/InvButton';
import { InvTextField } from '../../../components/InvField';
import PharmacyBatchPicker, { computeLinesTotal, batchLineQtyIssue } from '../../../components/PharmacyBatchPicker';
import SplitPaymentCollector from '../../../components/SplitPaymentCollector';
import ReceiptView from '../../../components/ReceiptView';
import ControlledAuthStatusPanel from '../components/ControlledAuthStatusPanel';
import { formatINR, formatDateTime, parseMoney } from '../../../utils';
import {
  applyOrderDiscountPercent,
  computeMrpOrderSummary,
  formatDiscountPct,
} from '../../../utils/pharmacyDiscount';
import { IPD_COLORS } from '../../../theme';
import { isControlledOrNarcotic } from '../../../utils/controlledDrug';
import { matchSaleItemForMedicineLine } from './useDirectDispensePipeline';
import useControlledAuthQueue from './useControlledAuthQueue';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

export const MEDICINE_LINE_DEFAULT = {
  medicine_id: '',
  medicine: null,
  batchLines: [],
  inventory_transaction_id: null,
  inventory_transaction_item_id: null,
};

/** How long to let the counter keep typing before syncing the draft sale. */
const SALE_SYNC_DEBOUNCE_MS = 700;

export function medicineLinesValid(lines) {
  return (lines || []).some((l) => l.batchLines?.length
    && l.batchLines.every((bl) => batchLineQtyIssue(bl) === null));
}

export function isControlledMedicineLine(line) {
  return isControlledOrNarcotic(line?.medicine) || Boolean(line?.medicine?.controlled);
}

export function isControlledLineReady(line) {
  if (!line?.batchLines?.length) return false;
  return line.batchLines.every((bl) => batchLineQtyIssue(bl) === null);
}

/**
 * A controlled line's status comes from the Controlled Queue, never from local
 * state: the pharmacist decides on the queue screen, and this screen only
 * reports what the service says.
 */
export function controlledLineStatus(line, authByLineId) {
  const lineId = line?.inventory_transaction_item_id;
  const row = lineId ? (authByLineId || {})[String(lineId)] : null;
  if (!row) return 'NOT_REQUESTED';
  return String(row.status || 'PENDING').toUpperCase();
}

export function hasUnapprovedControlledLines(lines, authByLineId) {
  return (lines || []).some(
    (l) => isControlledMedicineLine(l) && controlledLineStatus(l, authByLineId) !== 'AUTHORIZED',
  );
}

/** Signature of everything that would change the sale's lines. */
function basketSignature(lines) {
  return JSON.stringify(
    (lines || [])
      .filter((l) => l.medicine_id || l.medicine?.item_id)
      .map((l) => [
        l.medicine_id || l.medicine?.item_id,
        (l.batchLines || []).map((bl) => [
          bl.batch_number,
          bl.qty,
          bl.rate,
          bl.discount_percent,
        ]),
      ]),
  );
}

/**
 * Combined medicine selection + payment on one page
 * so cashiers can complete a direct dispense with minimal clicks.
 */
export default function DirectDispenseMedicineStep({
  lines,
  setLines,
  storeId,
  store = null,
  isSupervisor,
  serverTotals,
  pipeline,
  patientLabel,
  doctorName,
  onPrepareBill,
  onPrepareAndPay,
  onNewDispense,
  saleFinalized,
  saleRemarks = '',
  /** Quick-registered identity; a controlled line cannot ride on a nameless sale. */
  patientDisplay = null,
  /** When false, medicine search uses prescribe/search without location_id (walk-in). */
  requireStoreForSearch = true,
  /**
   * Exchange mode: `{ creditAmount }` is the refund being exchanged. The customer
   * pays only what the basket costs above it, and a basket below it is blocked.
   */
  exchange = null,
}) {
  const {
    state,
    toastError,
    ensureDraftSale,
  } = pipeline;

  const history = useHistory();
  const [prepareBillBusy, setPrepareBillBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [lastModes, setLastModes] = useState([]);
  const [queueSyncError, setQueueSyncError] = useState(null);
  const [queueSyncing, setQueueSyncing] = useState(false);
  const [syncAttempt, setSyncAttempt] = useState(0);
  const [discountPctInput, setDiscountPctInput] = useState(null);
  const syncedSignatureRef = useRef(null);
  // Serializes draft-sale syncs. A create is two sequential gateway round
  // trips and the debounce below is 700ms, so back-to-back basket edits would
  // otherwise both see a null saleId and each POST a sale -- two drafts, two
  // sets of controlled-auth requests, and the first one orphaned.
  const syncChainRef = useRef(Promise.resolve());

  const preview = computeLinesTotal(lines);
  const orderSummary = useMemo(() => computeMrpOrderSummary(lines), [lines]);
  const hasControlledLines = (lines || []).some(isControlledMedicineLine);
  const controlledLinesReady = (lines || [])
    .filter(isControlledMedicineLine)
    .every(isControlledLineReady);

  // Only the controlled lines that already carry a sale line id can have a
  // queue row; the hook uses these to know when it has heard about all of them.
  const controlledLineIds = (lines || [])
    .filter(isControlledMedicineLine)
    .map((l) => l.inventory_transaction_item_id)
    .filter(Boolean);

  const {
    authByLineId,
    loading: authLoading,
    error: authError,
    lastCheckedAt,
    reload: reloadControlledAuths,
  } = useControlledAuthQueue({
    saleId: state.saleId,
    scope: state.scope,
    enabled: hasControlledLines && !saleFinalized && controlledLineIds.length > 0,
  });

  const pendingControlledAuth = hasUnapprovedControlledLines(lines, authByLineId);
  const showServer = serverTotals?.amountPayable != null || state.amountPayable != null;
  const paid = state.billStatus === 'PAID' && state.paymentResult;
  const pipelineBusy = state.pipelineBusy;
  const basePayable = showServer
    ? parseMoney(serverTotals?.amountPayable ?? state.amountPayable)
    : preview.amountPayable;
  const payableDisplay = basePayable;
  const amountIsEstimated = !showServer;
  const exchangeCredit = exchange ? parseMoney(exchange.creditAmount) : 0;
  const exchangeShortfall = exchange
    ? Math.max(0, Math.round((exchangeCredit - payableDisplay) * 100) / 100)
    : 0;
  const amountDue = exchange
    ? Math.max(0, Math.round((payableDisplay - exchangeCredit) * 100) / 100)
    : payableDisplay;
  const canEditDiscount = !saleFinalized && !payBusy && !pipelineBusy
    && (lines || []).some((l) => (l.batchLines || []).some((bl) => Number(bl.qty) > 0));
  const discountPctDisplay = discountPctInput != null
    ? discountPctInput
    : formatDiscountPct(orderSummary.discountPct);

  function handleDiscountPctChange(raw) {
    setDiscountPctInput(raw);
    if (raw === '' || raw === '.') return;
    const pct = Number(raw);
    if (!Number.isFinite(pct)) return;
    setLines((prev) => applyOrderDiscountPercent(prev, pct));
  }

  // Link sale transaction item ids to lines after bill prep (no extra API on Authorize).
  useEffect(() => {
    const saleId = state.saleId;
    const saleItems = state.saleItems;
    if (!saleId || !saleItems?.length) return;

    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (!line.medicine_id && !line.medicine?.item_id) return line;
        const match = matchSaleItemForMedicineLine(line, saleItems);
        const txItemId = match?.transaction_item_id || match?.inventory_transaction_item_id;
        if (!txItemId) return line;
        if (
          line.inventory_transaction_id === saleId
          && line.inventory_transaction_item_id === txItemId
        ) {
          return line;
        }
        changed = true;
        return {
          ...line,
          inventory_transaction_id: saleId,
          inventory_transaction_item_id: txItemId,
        };
      });
      return changed ? next : prev;
    });
  }, [state.saleId, state.saleItems, setLines]);

  // A controlled line has to reach the pharmacist's queue on its own. Writing
  // the draft sale is what queues it (the service raises a PENDING request per
  // controlled line), so sync the draft as soon as the basket settles instead
  // of waiting for a button the cashier should not have to press.
  const signature = useMemo(() => basketSignature(lines), [lines]);
  // Parents build patientDisplay inline, so its identity changes every render.
  // Depend on its value instead, or the debounce timer would restart forever
  // and never fire.
  const patientKey = useMemo(() => JSON.stringify({
    name: patientDisplay?.name || '',
    mobile: patientDisplay?.mobile || patientDisplay?.phone || '',
  }), [patientDisplay]);
  const patientDisplayRef = useRef(patientDisplay);
  patientDisplayRef.current = patientDisplay;

  useEffect(() => {
    if (!hasControlledLines || saleFinalized || !storeId) return undefined;
    if (!controlledLinesReady || !medicineLinesValid(lines)) return undefined;
    if (syncedSignatureRef.current === signature) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setQueueSyncing(true);
      const run = syncChainRef.current
        // A failed sync must not poison every sync after it.
        .catch(() => {})
        .then(() => (cancelled ? null : ensureDraftSale({
          lines,
          storeId,
          store,
          remarks: saleRemarks,
          patientDisplay: patientDisplayRef.current,
        })));
      syncChainRef.current = run;
      try {
        await run;
        if (cancelled) return;
        syncedSignatureRef.current = signature;
        setQueueSyncError(null);
        await reloadControlledAuths();
      } catch (err) {
        if (!cancelled) setQueueSyncError(err?.message || 'Could not queue controlled medicines');
      } finally {
        if (!cancelled) setQueueSyncing(false);
      }
    }, SALE_SYNC_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `lines` is covered by `signature`; depending on it directly would re-run
    // on every unrelated re-render of the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    signature,
    hasControlledLines,
    controlledLinesReady,
    saleFinalized,
    storeId,
    store,
    saleRemarks,
    patientKey,
    ensureDraftSale,
    reloadControlledAuths,
    syncAttempt,
  ]);

  // The effect keys on the basket signature, so a failed sync never retries on
  // its own -- the cashier would be stuck with payment disabled and no way
  // forward short of rebuilding the basket.
  function retryQueueSync() {
    setQueueSyncError(null);
    syncedSignatureRef.current = null;
    setSyncAttempt((n) => n + 1);
  }

  // What the counter should see for each controlled line, in plain stages.
  const controlledRows = (lines || [])
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => isControlledMedicineLine(line))
    .map(({ line, index }) => {
      const status = controlledLineStatus(line, authByLineId);
      const auth = line.inventory_transaction_item_id
        ? authByLineId[String(line.inventory_transaction_item_id)]
        : null;
      const medicineName = line.medicine?.item_name || line.medicine?.name || 'Medicine';
      const qty = (line.batchLines || []).reduce((sum, bl) => sum + (Number(bl.qty) || 0), 0);

      let stage = 'SENDING';
      let detail = 'Writing this line to the pharmacist\u2019s queue\u2026';
      if (!isControlledLineReady(line)) {
        stage = 'SELECT';
        detail = 'Pick a batch and quantity — it goes to the queue automatically after that.';
      } else if (status === 'AUTHORIZED') {
        stage = 'AUTHORIZED';
        detail = `Authorized${auth?.supervisor_id ? ` by ${auth.supervisor_id}` : ''}`
          + `${auth?.authorized_at ? ` on ${formatDateTime(auth.authorized_at)}` : ''}.`;
      } else if (status === 'DENIED') {
        stage = 'DENIED';
        detail = `Denied${auth?.reason ? ` — ${auth.reason}` : ''}. `
          + 'Remove this medicine or change the selection to continue.';
      } else if (status === 'PENDING') {
        stage = 'PENDING';
        detail = 'Parked with the pharmacist — collect it from Controlled Orders once authorized.';
      }
      return { key: `${index}-${line.medicine_id || medicineName}`, medicineName, qty, stage, detail };
    });

  // Announce every transition once. The counter is looking at the basket, not
  // at a status column, so a silent flip to AUTHORIZED would go unnoticed.
  const announcedRef = useRef({});
  useEffect(() => {
    (lines || []).forEach((line) => {
      const lineId = line.inventory_transaction_item_id;
      if (!lineId || !isControlledMedicineLine(line)) return;
      const status = controlledLineStatus(line, authByLineId);
      const key = String(lineId);
      const previous = announcedRef.current[key];
      if (previous === status) return;
      announcedRef.current[key] = status;
      if (previous === undefined && status !== 'PENDING') return;
      const name = line.medicine?.item_name || line.medicine?.name || 'Controlled medicine';
      if (status === 'PENDING') toast.info(`${name} sent to the Controlled Queue`);
      else if (status === 'AUTHORIZED') toast.success(`${name} authorized — payment unlocked`);
      else if (status === 'DENIED') toast.error(`${name} authorization denied`);
    });
  }, [lines, authByLineId]);

  function openControlledOrders() {
    const target = window.location.pathname.replace(
      /(\/pharmacy)(\/.*)?$/,
      '$1/controlled-orders',
    );
    history.push(target);
  }

  function openControlledQueue() {
    // New tab on purpose: navigating away would throw away the basket, which
    // lives in this component's state.
    const queueUrl = window.location.pathname.replace(
      /(\/pharmacy)(\/.*)?$/,
      '$1/controlled-queue',
    );
    window.open(queueUrl, '_blank', 'noopener');
  }

  function updateLine(index, patch) {
    setLines((prev) => {
      const next = prev.map((row, idx) => (idx === index ? { ...row, ...patch } : row));
      if (patch.medicine_id && !prev[index].medicine_id) {
        const empty = next.findIndex((l, i) => i > index && !l.medicine_id);
        if (empty === -1) next.push({ ...MEDICINE_LINE_DEFAULT });
      }
      return next;
    });
  }

  async function handlePrepareBillOnly() {
    if (!medicineLinesValid(lines)) {
      toastError(new Error('Add at least one medicine with a valid quantity'));
      return;
    }
    if (!onPrepareBill) return;
    setPrepareBillBusy(true);
    try {
      await onPrepareBill();
    } catch (err) {
      toastError(err);
    } finally {
      setPrepareBillBusy(false);
    }
  }

  async function handlePayment(modes) {
    if (pendingControlledAuth) {
      toast.error('A pharmacist must authorize the controlled medicines in the Controlled Queue first');
      return;
    }
    setPayBusy(true);
    setLastModes(modes);
    try {
      await onPrepareAndPay(modes);
    } catch (err) {
      toastError(err);
    } finally {
      setPayBusy(false);
    }
  }

  const receiptLines = (lines || []).flatMap((line) => (line.batchLines || []).map((bl) => {
    const itemDiscount = computeLineDiscountAmountSafe(bl.qty, bl.rate, bl.discount_percent);
    const basic = (Number(bl.qty) || 0) * (Number(bl.rate) || 0);
    return {
      name: line.medicine?.item_name || line.medicine?.name,
      batch_number: bl.batch_number,
      expiry_date: bl.expiry_date,
      qty: bl.qty,
      rate: bl.rate,
      line_basic_amount: basic,
      item_discount_amount: itemDiscount,
      bill_discount_share_amount: 0,
      line_net_amount: Math.max(0, basic - itemDiscount),
    };
  }));
  const receiptLineDiscount = receiptLines.reduce((s, l) => s + parseMoney(l.item_discount_amount), 0);
  const receiptBillDiscount = 0;
  const receiptBasic = receiptLines.reduce((s, l) => s + parseMoney(l.line_basic_amount), 0);

  if (paid) {
    const receiptNumber = state.paymentResult?.receipt_number
      || state.paymentResult?.receipt_no
      || state.paymentResult?.id;
    return (
      <SectionCard title="Dispense complete">
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: IPD_COLORS.success.text, mb: 1 }} />
          <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 0.5 }}>Payment successful</Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 800, color: IPD_COLORS.primary, mb: 2 }}>
            {receiptNumber}
          </Typography>
          <Typography sx={{ fontSize: 14, mb: 3 }}>
            Collected {formatINR(state.amountPayable)}
          </Typography>
          <ReceiptView
            receiptNumber={receiptNumber}
            billNumber={state.billNumber || state.billId}
            patientLabel={patientLabel}
            doctorName={doctorName}
            lines={receiptLines}
            totalBasic={receiptBasic}
            lineDiscountTotal={receiptLineDiscount}
            billDiscount={receiptBillDiscount}
            amountPayable={state.amountPayable}
            modes={lastModes}
            createdAt={state.paymentResult?.created_at}
          />
          <Box sx={{ mt: 2 }}>
            <InvButton onClick={onNewDispense}>New dispense</InvButton>
          </Box>
        </Box>
      </SectionCard>
    );
  }

  return (
    <>
      {saleFinalized && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Stock already deducted for this dispense. Retry continues from the failed bill step — sale will not be recreated.
        </Alert>
      )}
      {state.lastError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.lastFailedStep ? `Failed at ${state.lastFailedStep}: ` : ''}{state.lastError}
        </Alert>
      )}
      {pipelineBusy && (
        <Alert severity="info" sx={{ mb: 2 }}>{state.pipelineLabel || 'Working…'}</Alert>
      )}
      {(queueSyncError || authError) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={queueSyncError ? (
            <InvButton size="small" variant="outlined" tone="red" onClick={retryQueueSync}>
              Try again
            </InvButton>
          ) : null}
        >
          {queueSyncError || authError}
        </Alert>
      )}
      {pendingControlledAuth && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          icon={<LockIcon fontSize="inherit" />}
          action={(
            <InvButton size="small" variant="outlined" tone="teal" onClick={openControlledOrders}>
              Controlled Orders
            </InvButton>
          )}
        >
          {state.saleId ? (
            <>
              Sent to the pharmacist. This order is <strong>parked</strong> — you can serve
              the next customer and collect payment from <strong>Controlled Orders</strong>
              once it is authorized.
            </>
          ) : (
            'Controlled medicines will be sent to the pharmacist for authorization.'
          )}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <ControlledAuthStatusPanel
          rows={controlledRows}
          loading={authLoading || queueSyncing}
          lastCheckedAt={lastCheckedAt}
          onRefresh={reloadControlledAuths}
          onOpenQueue={openControlledQueue}
        />
      </Box>

      <SectionCard
        title="Select medicines"
        subtitle="Search by brand, generic, or medicine name — pick batches, qty, and line discount"
        actions={
          <InvButton
            size="small"
            startIcon={<AddIcon />}
            disabled={pipelineBusy || payBusy}
            onClick={() => setLines((l) => [...l, { ...MEDICINE_LINE_DEFAULT }])}
          >
            Add row
          </InvButton>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {lines.map((line, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
              <IconButton
                size="small"
                disabled={lines.length <= 1 || pipelineBusy || payBusy || saleFinalized}
                onClick={() => setLines((l) => l.filter((_, idx) => idx !== i))}
                sx={{ color: IPD_COLORS.error.text, mt: { xs: 0.5, sm: 1 }, flexShrink: 0 }}
                aria-label="Remove medicine row"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <PharmacyBatchPicker
                  medicineLine={line}
                  storeId={storeId}
                  store={store}
                  isSupervisor={isSupervisor}
                  disabled={pipelineBusy || payBusy || saleFinalized}
                  requireStore={requireStoreForSearch}
                  onMedicineChange={(itemId, item) => updateLine(i, {
                    medicine_id: itemId,
                    medicine: item,
                    inventory_transaction_id: null,
                    inventory_transaction_item_id: null,
                  })}
                  onBatchLinesChange={(batchLines) => updateLine(i, {
                    batchLines,
                    inventory_transaction_id: null,
                    inventory_transaction_item_id: null,
                  })}
                />
                {isControlledMedicineLine(line) && (
                  <Box sx={{ mt: 1.25 }}>
                    {(() => {
                      const status = controlledLineStatus(line, authByLineId);
                      const auth = line.inventory_transaction_item_id
                        ? authByLineId[String(line.inventory_transaction_item_id)]
                        : null;
                      if (status === 'AUTHORIZED') {
                        return (
                          <Alert severity="success" sx={{ py: 0.25 }}>
                            Controlled substance authorized
                            {auth?.supervisor_id ? ` by ${auth.supervisor_id}` : ''}
                            {auth?.authorized_at ? ` · ${formatDateTime(auth.authorized_at)}` : ''}
                          </Alert>
                        );
                      }
                      if (status === 'DENIED') {
                        return (
                          <Alert severity="error" sx={{ py: 0.25 }}>
                            Authorization denied
                            {auth?.reason ? ` — ${auth.reason}` : ''}
                            . Remove this medicine or change the selection to continue.
                          </Alert>
                        );
                      }
                      if (status === 'PENDING') {
                        return (
                          <Alert
                            severity="info"
                            sx={{ py: 0.25 }}
                            icon={<LockIcon fontSize="inherit" />}
                          >
                            Waiting for pharmacist authorization in the Controlled Queue.
                          </Alert>
                        );
                      }
                      if (!isControlledLineReady(line)) {
                        return (
                          <Alert severity="warning" sx={{ py: 0.25 }}>
                            Controlled substance — select batch and quantity to send it
                            to the Controlled Queue.
                          </Alert>
                        );
                      }
                      return (
                        <Alert
                          severity="warning"
                          sx={{ py: 0.25, display: 'flex', alignItems: 'center' }}
                          icon={
                            queueSyncing || authLoading
                              ? <CircularProgress size={14} />
                              : <LockIcon fontSize="inherit" />
                          }
                        >
                          Sending this controlled substance to the Controlled Queue…
                        </Alert>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </SectionCard>

      <Grid container spacing={2} sx={{ mt: 2 }} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <SectionCard title="Order Summary" sx={{ height: '100%' }}>
            <SummaryRow label="Subtotal" value={formatINR(orderSummary.mrpSubtotal)} />
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              py: 0.4,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: IPD_COLORS.textPrimary }}>
                  Discount
                </Typography>
                <Box sx={{ width: 72 }}>
                  <InvTextField
                    type="number"
                    value={discountPctDisplay}
                    disabled={!canEditDiscount}
                    inputProps={{ min: 0, max: 100, step: '0.01', style: { textAlign: 'right' } }}
                    onFocus={() => setDiscountPctInput(formatDiscountPct(orderSummary.discountPct))}
                    onChange={(e) => handleDiscountPctChange(e.target.value)}
                    onBlur={() => setDiscountPctInput(null)}
                  />
                </Box>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>%</Typography>
              </Box>
              <Typography sx={{
                fontSize: 13,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: IPD_COLORS.textPrimary,
              }}>
                − {formatINR(orderSummary.discount)}
              </Typography>
            </Box>
            <Box sx={{ borderTop: `1px solid ${IPD_COLORS.border}`, my: 1 }} />
            <SummaryRow
              label={amountIsEstimated ? 'Estimated Amount Payable' : 'Amount Payable'}
              value={formatINR(payableDisplay)}
              accent
            />
            {exchange && (
              <>
                <SummaryRow label="Refund credit applied" value={`− ${formatINR(exchangeCredit)}`} />
                <SummaryRow label="Customer pays" value={formatINR(amountDue)} accent />
              </>
            )}
            {amountIsEstimated && (
              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.75 }}>
                Final amount will be confirmed at payment.
              </Typography>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard
            title={exchange ? 'Exchange & Payment' : 'Payment & Dispense'}
            sx={{ height: '100%' }}
          >
            {exchange && exchangeShortfall > 0 && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                Replacement items are worth {formatINR(payableDisplay)}, which is{' '}
                {formatINR(exchangeShortfall)} less than the refund of {formatINR(exchangeCredit)}.
                Add items worth at least {formatINR(exchangeShortfall)} more — cash cannot be returned.
              </Alert>
            )}
            {exchange && exchangeShortfall === 0 && amountDue === 0 && (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                Replacement items match the refund exactly — no payment is due.
              </Alert>
            )}
            {exchange && amountDue === 0 ? (
              <InvButton
                fullWidth
                loading={payBusy || pipelineBusy}
                disabled={
                  exchangeShortfall > 0
                  || pendingControlledAuth
                  || !medicineLinesValid(lines)
                  || !storeId
                }
                onClick={() => handlePayment([])}
              >
                Complete exchange
              </InvButton>
            ) : (
              <SplitPaymentCollector
                totalDue={amountDue}
                direction="collect"
                autoFill
                compact
                hideHeading
                remainingLabel="Amount Remaining"
                modeLabel="Payment Method"
                amountLabel={exchange ? 'Amount to Pay' : 'Amount to Collect'}
                submitting={payBusy || pipelineBusy}
                disabled={
                  exchangeShortfall > 0
                  || pendingControlledAuth
                  || !medicineLinesValid(lines)
                  || !storeId
                }
                onSubmit={handlePayment}
              />
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/*
      <SupervisorApprovalModal
        open={approvalOpen}
        onClose={() => setApprovalOpen(false)}
        title="Approve bill discount"
        ...
      />
      */}

    </>
  );
}

function computeLineDiscountAmountSafe(qty, rate, discPct) {
  const q = Number(qty) || 0;
  const r = Number(rate) || 0;
  const d = Number(discPct) || 0;
  return q * r * (d / 100);
}

function SummaryRow({ label, value, accent = false }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.4 }}>
      <Typography sx={{
        fontSize: accent ? 13 : 12,
        fontWeight: accent ? 700 : 500,
        color: IPD_COLORS.textPrimary,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: accent ? 16 : 13,
        fontWeight: accent ? 800 : 600,
        fontVariantNumeric: 'tabular-nums',
        color: accent ? IPD_COLORS.primary : IPD_COLORS.textPrimary,
      }}>
        {value}
      </Typography>
    </Box>
  );
}
